// The swap card's one primary button: what it says and does at each point, so the user is walked
// through connect → switch network → (approve, inside the open) → swap, and never wonders why it
// is disabled.

import type { OpenPhase } from "./open";

export type ActionKind = "connect" | "switch" | "quote" | "open" | "none";
export type Action = { label: string; kind: ActionKind; disabled: boolean; busy?: boolean };

/** Why the form cannot be quoted yet. `amount` and `recipient` have their own button wording;
 *  anything else (`other`) is shown as is. */
export type FormIssue = { kind: "amount" | "recipient" | "other"; message: string };

export type ActionInput = {
  issue: FormIssue | null;
  /** the source chain's wallet (or Jupnet key) is connected */
  connected: boolean;
  /** "EVM wallet", "Solana wallet", "Sui wallet", "Jupnet key" */
  walletName: string;
  /** an EVM wallet on another chain than the source: the label of the source */
  switchTo?: string | null;
  /** the amount is more than the wallet holds (when the balance is known) */
  insufficient?: string | null;
  quote: { loading: boolean; ready: boolean; failed: boolean };
  /** an open is running, at this phase */
  phase?: OpenPhase | null;
  /** the token an approval is for, for the phase label */
  approveSym?: string;
  /** "Swap", "Withdraw to Base", … */
  verb: string;
};

export const PHASE_LABEL: Record<OpenPhase, (sym: string) => string> = {
  switch: () => "Switch network in your wallet…",
  build: () => "Preparing your swap…",
  approve: (sym) => `Approve ${sym} in your wallet…`,
  sign: () => "Confirm in your wallet…",
  submit: () => "Submitting…",
  confirm: () => "Waiting for confirmation…",
};

export function primaryAction(a: ActionInput): Action {
  if (a.phase) return { label: PHASE_LABEL[a.phase](a.approveSym ?? "the token"), kind: "none", disabled: true, busy: true };
  if (a.issue?.kind === "amount") return { label: "Enter an amount", kind: "none", disabled: true };
  if (!a.connected) return { label: `Connect ${a.walletName}`, kind: "connect", disabled: false };
  if (a.issue?.kind === "recipient") return { label: a.issue.message || "Add a recipient", kind: "connect", disabled: false };
  if (a.issue) return { label: a.issue.message, kind: "none", disabled: true };
  if (a.insufficient) return { label: `Insufficient ${a.insufficient}`, kind: "none", disabled: true };
  if (a.switchTo) return { label: `Switch to ${a.switchTo}`, kind: "switch", disabled: false };
  if (a.quote.ready) return { label: a.verb, kind: "open", disabled: false };
  if (a.quote.loading) return { label: "Finding the best price…", kind: "none", disabled: true, busy: true };
  if (a.quote.failed) return { label: "Try again", kind: "quote", disabled: false };
  return { label: "Get quote", kind: "quote", disabled: false };
}
